import { useRef, useState } from 'react';
import { ActionButton } from '../../../parts/index.js';
import { ApiError } from '../../../api.js';
import type { Action } from '../contract.js';
import { NotSaved, Refused } from './DrawingStates.js';
import { Change } from './DrawingChange.js';
import { park, leave, post } from '../home/api.js';
import { actionAddress, navigate } from '../home/addresses.js';

export function ActionControl({ action, store, itemKey, lead = false, onChanged, onExpanded }: {
  action: Action; store: string; itemKey?: string | undefined; lead?: boolean;
  // stale: the server answered 409 (the item changed meanwhile); the page reloads instead of hiding it (brief 6.8).
  onChanged?: ((words?: string, stale?: boolean) => void) | undefined;
  onExpanded?: ((expanded: boolean) => void) | undefined;
}) {
  const label = action.kind === 'park' ? 'Park until...' : action.kind === 'leave' ? 'Leave it' : action.label;
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const retry = useRef<(() => Promise<void>) | null>(null);
  const posted = useRef<{ done?: string } | undefined>(undefined);
  // The pause went away meanwhile (404: the switch was turned off): close and reload, never draw "not found" (round 11).
  const gone = useRef(false);
  const inflight = useRef(false);

  async function run(work: () => Promise<void>) {
    if (inflight.current) return;
    inflight.current = true; retry.current = work; setBusy(true); setError(null);
    try { await work(); setExpanded(false); onExpanded?.(false); } catch (e) {
      if (e instanceof ApiError && e.status === 409) { setExpanded(false); onExpanded?.(false); onChanged?.(e.message, true); }
      else setError(e);
    }
    finally { inflight.current = false; setBusy(false); }
  }
  function press() {
    if (action.kind === 'open') { navigate(actionAddress(action)); return; }
    if (action.kind === 'park' || action.kind === 'post') { setExpanded(true); onExpanded?.(true); return; }
    if (action.kind === 'copy') {
      void run(async () => { await navigator.clipboard.writeText(action.text); setCopied(true); }); return;
    }
    if (action.kind === 'leave' && itemKey) void run(async () => { await leave(store, itemKey); onChanged?.(); });
  }
  return <>
    {!(expanded && action.kind === 'post') && <ActionButton className={lead ? 'opt lead' : 'opt'}
      reading={action.kind === 'open' && !action.open} disabled={busy} onClick={press}>{label}</ActionButton>}
    {expanded && action.kind === 'park' && itemKey && <div className="opts">
      {([['tomorrow', 'Tomorrow morning'], ['monday', 'Monday morning'], ['week', 'In a week']] as const).map(([until, words]) =>
        <ActionButton className="opt" key={until} disabled={busy} onClick={() => void run(async () => {
          await park(store, itemKey, until); onChanged?.();
        })}>{words}</ActionButton>)}
    </div>}
    {/* A post writes, so it asks its reason in the box Sources' pause uses (brief 4.2; the referee, 15 Oct). */}
    {expanded && action.kind === 'post' && <Change label={label} sentence={action.confirm}
      onSave={async reason => {
        gone.current = false;
        try { posted.current = await post(store, action.path, reason); }
        catch (e) { if (e instanceof ApiError && e.status === 404) { gone.current = true; return; } throw e; }
      }}
      onSaved={() => { setExpanded(false); onExpanded?.(false); if (gone.current) onChanged?.(undefined, true); else onChanged?.(posted.current?.done ?? action.confirm); }}
      onCancel={() => { setExpanded(false); onExpanded?.(false); }}
      onConflict={() => { setExpanded(false); onExpanded?.(false); onChanged?.(undefined, true); }} />}
    {copied && <p className="okline" role="status">Copied</p>}
    {error != null && (error instanceof ApiError && error.status === 403
      ? <Refused words={error.message} />
      : <NotSaved reason={error instanceof Error ? error.message : String(error)} onRetry={() => { if (retry.current) void run(retry.current); }} />)}
  </>;
}
